// Import 3rd-party dependencies
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormGroup, FormBuilder, FormArray, Validators, AbstractControl, FormControl } from '@angular/forms';

// Import project-specific dependencies
import { IResourceLink } from '@app/services/app-tools/app-tools.service';
import { IFavoriteResourceLink } from '@app/services/favorites/favorites.service';
import { urlValidator } from '@app/shared/validators/url.validator';
import { ADD_FAVORITES_DIALOG_COMPONENT_CONSTS } from '@app/config/constants';

enum Validity {
  VALID,
  INVALID_EMPTY,
  INVALID_TRUE
}

@Component({
  selector: 'app-add-favorite-dialog',
  templateUrl: './add-favorite-dialog.component.html',
  styleUrls: ['./add-favorite-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddFavoriteDialogComponent implements OnInit {
  consts = ADD_FAVORITES_DIALOG_COMPONENT_CONSTS;
  favForm: FormGroup;

  constructor(public dialogRef: MatDialogRef<AddFavoriteDialogComponent>, private fb: FormBuilder) {}

  ngOnInit(): void {
    this.favForm = this.fb.group({
      customItems: this.fb.array([this.createCustomItemControl()]),
      toolsItems: this.fb.array([this.createToolsControl()])
    });
  }

  createToolsControl(): FormControl {
    return this.fb.control([null, Validators.required]); // eslint-disable-line @typescript-eslint/unbound-method
  }

  createCustomItemControl(): FormGroup {
    return this.fb.group({
      link: [
        null,
        [
          Validators.required, // eslint-disable-line @typescript-eslint/unbound-method
          urlValidator
        ]
      ],
      name: [
        null,
        Validators.required // eslint-disable-line @typescript-eslint/unbound-method
      ]
    });
  }

  get customFavFormArray(): FormArray {
    return this.favForm.get('customItems') as FormArray;
  }

  get toolsFavFormArray(): FormArray {
    return this.favForm.get('toolsItems') as FormArray;
  }

  get listTools(): string[] {
    return this.toolsFavFormArray.controls.map((control) => (<IResourceLink>control.value).name);
  }

  get canAddCustomFav(): boolean {
    return this.customFavFormArray.length < this.consts.FIELDS_MAX_COUNT;
  }

  get canAddToolsFav(): boolean {
    return this.toolsFavFormArray.length < this.consts.FIELDS_MAX_COUNT;
  }

  addCustomFavControl(): void {
    if (this.canAddCustomFav) {
      this.customFavFormArray.push(this.createCustomItemControl());
      this.customFavFormArray.markAsUntouched();
    }
  }

  addToolsFavControl(): void {
    if (this.canAddToolsFav) {
      this.toolsFavFormArray.push(this.createToolsControl());
    }
  }

  removeCustomFavAt(i: number): void {
    this.customFavFormArray.removeAt(i);
  }

  removeFavAt(i: number): void {
    this.toolsFavFormArray.removeAt(i);
  }

  onClose(): void {
    this.dialogRef.close(null);
  }

  checkControlGroup(formArray: FormArray, isControlEmpty: (control: AbstractControl) => boolean): Validity {
    const isFormArrayInvalid = formArray.invalid;
    if (!isFormArrayInvalid) {
      return Validity.VALID;
    }

    if (formArray.length <= 1) {
      try {
        const formItem = formArray.controls[0];
        return isControlEmpty(formItem) ? Validity.INVALID_EMPTY : Validity.INVALID_TRUE;
      } catch (error) {
        return Validity.INVALID_EMPTY;
      }
    }

    return Validity.INVALID_TRUE;
  }

  validate(): boolean {
    // First check
    const customControlEmptyCheck = (control: AbstractControl): boolean => {
      return !(!!control.get('link').value || !!control.get('name').value);
    };

    const toolsControlEmptyCheck = (control: AbstractControl): boolean => {
      return !!control.value;
    };

    const toolsFormValid = this.checkControlGroup(this.toolsFavFormArray, toolsControlEmptyCheck);
    const customFormValid = this.checkControlGroup(this.customFavFormArray, customControlEmptyCheck);

    if (
      (toolsFormValid === Validity.VALID && customFormValid === Validity.VALID) ||
      (toolsFormValid === Validity.VALID && customFormValid === Validity.INVALID_EMPTY) ||
      (customFormValid === Validity.VALID && toolsFormValid === Validity.INVALID_EMPTY)
    ) {
      return true;
    }
    return false;
  }

  onSave(): void {
    const valid = this.validate();

    const customFavData = this.getCustomFavFormData();
    const toolsFavData = this.getToolsFavFormData();

    if (valid && !(customFavData.length === 0 && toolsFavData.length === 0)) {
      this.dialogRef.close([...customFavData, ...toolsFavData]);
    }
  }

  mapControlToFavorite(control: AbstractControl, customLink: boolean): IFavoriteResourceLink {
    const name = control.get('name')?.value as string;
    const link = control.get('link')?.value as string;
    
    return {
      customLink,
      name,
      link,
      dateTimeFavorited: new Date()
    };
  }

  getCustomFavFormData(): IFavoriteResourceLink[] {
    return this.customFavFormArray.controls
      .map((control) => this.mapControlToFavorite(control, true))
      .filter((i) => i.name);
  }

  getToolsFavFormData(): IFavoriteResourceLink[] {
    return this.toolsFavFormArray.controls
      .map((control) => {
        const { name, link, keywords } = control.value as IResourceLink;
        return {
          name,
          link,
          keywords,
          customLink: false,
          dateTimeFavorited: new Date()
        } as IFavoriteResourceLink;
      })
      .filter((i) => i.name);
  }
}
